/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Box,
  Button,
  ButtonGroup,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  GridItem,
  HStack,
  Icon,
  Input,
  Text,
  useDisclosure,
  VStack,
  useBreakpointValue,
  Radio,
  Tooltip,
} from '@chakra-ui/react';
import {
  Controller,
  useFieldArray,
  Control,
  FieldErrors,
  UseFormWatch,
  UseFormSetValue,
} from 'react-hook-form';
import { useGetSizes, useRemoveVariantSize } from '@/services/addProduct';
import DeleteIcon from '@/assets/svg/delete.svg?react';
import ConfirmDeleteIcon from '@/assets/svg/delete-bg.svg?react';
import InfoCircleIcon from '@/assets/svg/info-circle.svg?react';
import CustomConfirmationModal from '@/components/common/ConfirmationModal';
import { useRef, useState, useEffect } from 'react';
import Asterisk from '../common/Asterisk';
import { type FormData } from '@/services/addProduct/types';
import LoadingComponent from '../common/Loading';
import { useWatch } from 'react-hook-form';
import { AxiosError } from 'axios';

interface SizeOption {
  id: number | string;
  value: string;
}

interface SizeDetailsSectionProps {
  control: Control<FormData>;
  errors: FieldErrors<FormData>;
  index: number;
  item: FormData['variants'][number];
  update: (index: number, value: FormData['variants'][number]) => void;
  id?: string;
  showToast: (type: 'success' | 'error', message: string) => void;
  watch: UseFormWatch<FormData>;
  setValue: UseFormSetValue<FormData>;
  vendorCurrency?: string;
}

const SizeDetailsSection = ({
  control,
  errors,
  index,
  id,
  showToast,
  watch,
  setValue,
  vendorCurrency,
}: SizeDetailsSectionProps) => {
  const { data: sizes = [], isLoading: sizesLoading } = useGetSizes();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { mutateAsync: removeVariantSize } = useRemoveVariantSize();
  const [deleteSizeInfo, setDeleteSizeInfo] = useState<{
    sizeIndex: number;
    sizeId: number;
    isLastSize: boolean;
  } | null>(null);
  const addSizeLinkRef = useRef<HTMLButtonElement>(null);
  const currencySymbol = vendorCurrency && vendorCurrency === 'NPR' ? 'Rs.' : '$';
  const discountPercent =
    useWatch({
      control,
      name: 'productDetails.discount',
    }) ?? 0;
  const isCustomizable = useWatch({
    control,
    name: 'is_customizable',
  });
  const [, setFocusedFields] = useState<Record<string, boolean>>({});

  const gridColumns = useBreakpointValue({ base: '1fr', md: 'repeat(2, 1fr)' });
  const spacing = useBreakpointValue({ base: 2, md: 4 });
  const labelFontSize = useBreakpointValue({ base: 'xs', md: 'sm' });
  const buttonSize = useBreakpointValue({ base: 'sm', md: 'md' });
  const currentVariantSizes = useWatch({
    control,
    name: `variants.${index}.sizes`,
  }) as FormData['variants'][number]['sizes'];
  const {
    fields: sizeFields,
    append: appendSize,
    remove: removeSize,
  } = useFieldArray({
    control,
    name: `variants.${index}.sizes`,
  });
  const [isFirstFreeOrCustom, setIsFirstFreeOrCustom] = useState(false);
  const firstSelectedSize = watch('variants.0.sizes.0.size');

  useEffect(() => {
    const firstSizeObj = sizes.find((s: any) => String(s.id) === String(firstSelectedSize));
    const result =
      firstSizeObj?.value === 'Free Size' || firstSizeObj?.value === 'Custom Size Only';

    setIsFirstFreeOrCustom(result);
  }, [firstSelectedSize, sizes]);
  useEffect(() => {
    if (!isFirstFreeOrCustom) return;

    const firstSizeId = watch(`variants.0.sizes.0.size`);
    if (!firstSizeId) return;

    const allVariants = watch('variants');

    allVariants?.forEach((variant, vIndex) => {
      if (vIndex === 0) return;

      const currentSizes = variant.sizes || [];

      if (currentSizes.length !== 1 || currentSizes[0].size !== firstSizeId) {
        setValue(
          `variants.${vIndex}.sizes`,
          [
            {
              ...currentSizes[0],
              size: firstSizeId,
              isPrimary: false,
            },
          ],
          { shouldDirty: true },
        );
      }
    });
  }, [isFirstFreeOrCustom, watch('variants.0.sizes.0.size')]);

  useEffect(() => {
    setIsSingleSizeMode(isFirstFreeOrCustom);
  }, [isFirstFreeOrCustom]);

  const sizesWatch = watch(`variants.${index}.sizes`) as
    | (FormData['variants'][number]['sizes'][number] & { isPrimary?: boolean })[]
    | undefined;

  const primarySizeIndex = sizesWatch ? sizesWatch.findIndex((s) => s.isPrimary === true) : -1;

  useEffect(() => {
    if (index === 0 && sizesWatch && sizesWatch.length > 0) {
      const hasPrimary = sizesWatch.some((s) => s.isPrimary === true);
      if (!hasPrimary) {
        setValue(`variants.${index}.sizes.${0}.isPrimary`, true, { shouldDirty: true });
      }
    }
  }, [sizesWatch?.length ?? 0, index, setValue]);

  const getSelectedSizes = (currentVariantIndex: number, currentSizeIndex: number): string[] => {
    const selectedSizes: string[] = [];
    const variant = watch('variants')[currentVariantIndex] as FormData['variants'][number];

    if (variant && Array.isArray(variant.sizes)) {
      variant.sizes.forEach((size, sizeIndex) => {
        if (size.size && sizeIndex !== currentSizeIndex) {
          selectedSizes.push(String(size.size));
        }
      });
    }
    return selectedSizes;
  };

  const addSize = () => {
    if (sizesLoading) {
      showToast('error', 'Sizes are still loading');
      return;
    }

    if (sizeFields.length >= sizes.length) {
      showToast('error', `You can only add up to ${sizes.length} sizes`);
      return;
    }

    appendSize({
      id: 0,
      size: '',
      variantPrice: 0,
      stockQuantity: 0,
      weight: '',
      isPrimary: false,
      sku: '',
    });
  };

  const { remove: removeVariant } = useFieldArray({
    control,
    name: 'variants',
  });

  const handleVariantSizeDelete = (sizeIndex: number, length: number) => {
    if (length === 1) {
      showToast('error', 'At least one size is required');
      return;
    }
    if (index === 0 && sizeIndex === primarySizeIndex) {
      showToast('error', 'Cannot delete the primary size');
      return;
    }
    const actualSizeId = watch(`variants.${index}.sizes.${sizeIndex}.id`) || 0;
    setDeleteSizeInfo({ sizeIndex, sizeId: actualSizeId, isLastSize: length === 1 });
    onOpen();
  };

  const handleVariantSizeDeleteConfirm = () => {
    if (!deleteSizeInfo) return;

    const { sizeIndex, sizeId, isLastSize } = deleteSizeInfo;

    if (id && sizeId !== 0) {
      removeVariantSize([sizeId])
        .then(() => {
          removeSize(sizeIndex);
          if (isLastSize) {
            removeVariant(index);
          }
          onClose();
          setDeleteSizeInfo(null);
          showToast('success', 'Size removed successfully');
        })
        .catch((error: unknown) => {
          if (error instanceof AxiosError) {
            showToast('error', error?.response?.data?.[0] || 'Failed to remove size');
          }
          onClose();
          setDeleteSizeInfo(null);
        });
    } else {
      removeSize(sizeIndex);
      if (isLastSize) {
        removeVariant(index);
      }
      onClose();
      setDeleteSizeInfo(null);
      showToast('success', 'Size removed successfully');
    }
  };

  const handlePrimaryChange = (newSizeIndex: number) => {
    if (index !== 0) return;
    sizeFields.forEach((_, i) => {
      setValue(`variants.${index}.sizes.${i}.isPrimary`, false, { shouldDirty: true });
    });
    setValue(`variants.${index}.sizes.${newSizeIndex}.isPrimary`, true, { shouldDirty: true });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
    }
  };

  const handleFocus = (fieldName: string) => {
    setFocusedFields((prev) => ({ ...prev, [fieldName]: true }));
  };

  const handleBlur = (fieldName: string) => {
    setFocusedFields((prev) => ({ ...prev, [fieldName]: false }));
  };
  const [isSingleSizeMode, setIsSingleSizeMode] = useState(false);

  useEffect(() => {
    setIsSingleSizeMode(isFirstFreeOrCustom);
  }, [isFirstFreeOrCustom]);
  if (sizesLoading) return <LoadingComponent />;
  let firstSizeId: string | number | undefined = undefined;
  return (
    <Box
      border="1px solid"
      borderColor="border.default60"
      bg="background.secondary"
      borderRadius="lg"
      p={{ base: 2, md: 4 }}
    >
      <Flex direction="column" gap={spacing}>
        <Text fontSize={{ base: 'sm', md: 'md' }} fontWeight="semibold">
          Size Details
        </Text>

        {sizeFields.map((size, sizeIndex) => (
          <Flex
            key={size.id}
            border="1px solid"
            borderColor="border.default60"
            bg="background.white"
            borderRadius="lg"
            p={{ base: 2, md: 4 }}
            direction="column"
            gap={spacing}
          >
            <VStack spacing={spacing} align="stretch">
              <Grid templateColumns={gridColumns} gap={spacing}>
                <GridItem>
                  <FormControl isInvalid={!!errors.variants?.[index]?.sizes?.[sizeIndex]?.size}>
                    <FormLabel fontSize={labelFontSize} color="text.secondary">
                      Size
                      <Asterisk />
                    </FormLabel>
                    <Controller
                      name={`variants.${index}.sizes.${sizeIndex}.size`}
                      control={control}
                      render={({ field }) => (
                        <ButtonGroup
                          variant="outline"
                          spacing={2}
                          flexWrap="wrap"
                          justifyContent={{ base: 'center', md: 'flex-start' }}
                        >
                          {sizes
                            .filter((sizeOption: SizeOption) => {
                              firstSizeId = watch('variants.0.sizes.0.size');
                              const firstSelectedSizeValue = sizes.find(
                                (s: any) => String(s.id) === String(firstSelectedSize),
                              )?.value;

                              if (isFirstFreeOrCustom) {
                                if (index > 0) {
                                  return true;
                                }
                                return true;
                              }
                              if (index > 0) {
                                return !['Free Size', 'Custom Size Only'].includes(
                                  sizeOption.value,
                                );
                              }
                              if (
                                sizeIndex > 0 &&
                                !['Free Size', 'Custom Size Only'].includes(firstSelectedSizeValue)
                              ) {
                                return !['Free Size', 'Custom Size Only'].includes(
                                  sizeOption.value,
                                );
                              }
                              return true;
                            })
                            .map((sizeOption: SizeOption) => {
                              const sizeIdStr = String(sizeOption.id);
                              let isSelected = String(field.value) === sizeIdStr;
                              let isDisabled = getSelectedSizes(index, sizeIndex).includes(
                                sizeIdStr,
                              );
                              if (isFirstFreeOrCustom && index > 0) {
                                isDisabled = String(sizeIdStr) !== String(firstSizeId);
                                isSelected = String(sizeIdStr) === String(firstSizeId);
                              }

                              return (
                                <Button
                                  key={sizeOption.id}
                                  onClick={() => {
                                    const newSizeId = sizeIdStr;
                                    const newSizeValue = sizeOption.value;

                                    // Only update the .size field — this is the size reference
                                    field.onChange(newSizeId);

                                    // Handle Free Size / Custom Size Only logic
                                    if (
                                      newSizeValue === 'Free Size' ||
                                      newSizeValue === 'Custom Size Only'
                                    ) {
                                      setIsSingleSizeMode(true);
                                      setValue(
                                        `variants.${index}.sizes`,
                                        [
                                          {
                                            ...watch(`variants.${index}.sizes.${sizeIndex}`),
                                            size: newSizeId,
                                            // ← REMOVE id: Number(newSizeId) completely
                                            isPrimary: index === 0 && sizeIndex === 0,
                                          },
                                        ],
                                        { shouldDirty: true },
                                      );
                                    } else {
                                      setIsSingleSizeMode(false);
                                    }
                                  }}
                                  isDisabled={isDisabled && !isSelected}
                                  bg={isSelected ? 'red.100' : 'white'}
                                  color={isSelected ? 'brand.primary.DEFAULT' : 'black'}
                                  borderWidth="1px"
                                  borderColor={
                                    errors.variants?.[index]?.sizes?.[sizeIndex]?.size
                                      ? 'status.error'
                                      : isSelected
                                        ? 'brand.primary.DEFAULT'
                                        : 'border.default60'
                                  }
                                  _hover={{ bg: isSelected ? 'red.100' : 'white' }}
                                  rounded="lg"
                                  m={1}
                                  size={buttonSize}
                                >
                                  {sizeOption.value}
                                </Button>
                              );
                            })}
                        </ButtonGroup>
                      )}
                    />
                    <FormErrorMessage>
                      {errors.variants?.[index]?.sizes?.[sizeIndex]?.size?.message ||
                        'Size is required'}
                    </FormErrorMessage>
                  </FormControl>
                </GridItem>
                <GridItem>
                  <VStack spacing={spacing} align="stretch">
                    <Grid
                      templateColumns={{ base: '1fr', lg: 'repeat(3, 1fr)' }}
                      gap={spacing}
                      alignItems="start"
                    >
                      <GridItem>
                        <FormControl
                          isInvalid={!!errors.variants?.[index]?.sizes?.[sizeIndex]?.variantPrice}
                        >
                          <FormLabel fontSize={labelFontSize} color="text.secondary">
                            Variant Price
                            <Asterisk />
                          </FormLabel>
                          <Box position="relative">
                            <Text
                              position="absolute"
                              left="10px"
                              top="50%"
                              transform="translateY(-50%)"
                              color="text.primary"
                              pointerEvents="none"
                              zIndex={1}
                            >
                              {currencySymbol}
                            </Text>
                            <Controller
                              name={`variants.${index}.sizes.${sizeIndex}.variantPrice`}
                              control={control}
                              render={({ field }) => {
                                const fieldKey = `variantPrice-${sizeIndex}`;
                                const displayValue = field.value ?? '';

                                return (
                                  <Input
                                    type="number"
                                    min="0"
                                    step="any"
                                    sx={{
                                      '::-webkit-inner-spin-button': { display: 'none' },
                                      '::-webkit-outer-spin-button': { display: 'none' },
                                      paddingLeft: '30px',
                                    }}
                                    onKeyDown={handleKeyDown}
                                    value={displayValue}
                                    onClick={() => {
                                      if (field.value === 0 || field.value === 0) {
                                        field.onChange('');
                                      }
                                    }}
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      field.onChange(value === '' ? '' : Number(value));
                                    }}
                                    onFocus={() => handleFocus(fieldKey)}
                                    onBlur={() => handleBlur(fieldKey)}
                                    size={buttonSize}
                                  />
                                );
                              }}
                            />
                          </Box>
                          <FormErrorMessage>
                            {errors.variants?.[index]?.sizes?.[sizeIndex]?.variantPrice?.message}
                          </FormErrorMessage>
                        </FormControl>
                      </GridItem>

                      <GridItem>
                        <FormControl
                          isInvalid={!!errors.variants?.[index]?.sizes?.[sizeIndex]?.stockQuantity}
                        >
                          <FormLabel fontSize={labelFontSize} color="text.secondary">
                            Stock Quantity
                            <Asterisk />
                          </FormLabel>
                          <Controller
                            name={`variants.${index}.sizes.${sizeIndex}.stockQuantity`}
                            control={control}
                            render={({ field }) => {
                              const fieldKey = `stockQuantity-${sizeIndex}`;
                              const displayValue = field.value ?? '';

                              return (
                                <Input
                                  type="number"
                                  min="0"
                                  step="any"
                                  sx={{
                                    '::-webkit-inner-spin-button': { display: 'none' },
                                    '::-webkit-outer-spin-button': { display: 'none' },
                                  }}
                                  onKeyDown={handleKeyDown}
                                  value={displayValue}
                                  onClick={() => {
                                    if (field.value === 0 || field.value === 0) {
                                      field.onChange('');
                                    }
                                  }}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    field.onChange(value === '' ? '' : Number(value));
                                  }}
                                  onFocus={() => handleFocus(fieldKey)}
                                  onBlur={() => handleBlur(fieldKey)}
                                  size={buttonSize}
                                />
                              );
                            }}
                          />
                          <FormErrorMessage>
                            {errors.variants?.[index]?.sizes?.[sizeIndex]?.stockQuantity?.message}
                          </FormErrorMessage>
                        </FormControl>
                      </GridItem>

                      <GridItem>
                        <FormControl
                          isInvalid={!!errors.variants?.[index]?.sizes?.[sizeIndex]?.weight}
                        >
                          <FormLabel fontSize={labelFontSize} color="text.secondary">
                            Weight (kg)
                            <Asterisk />
                          </FormLabel>
                          <Controller
                            name={`variants.${index}.sizes.${sizeIndex}.weight`}
                            control={control}
                            render={({ field }) => {
                              const fieldKey = `weight-${sizeIndex}`;
                              const displayValue = field.value ?? '';

                              return (
                                <Input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  sx={{
                                    '::-webkit-inner-spin-button': { display: 'none' },
                                    '::-webkit-outer-spin-button': { display: 'none' },
                                  }}
                                  onKeyDown={handleKeyDown}
                                  value={displayValue}
                                  onClick={() => {
                                    if (field.value === '0.00' || field.value === '0') {
                                      field.onChange('');
                                    }
                                  }}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    if (value.includes('.')) {
                                      const parts = value.split('.');
                                      if (parts[1].length > 2) return;
                                    }
                                    field.onChange(value);
                                  }}
                                  onFocus={() => handleFocus(fieldKey)}
                                  onBlur={() => handleBlur(fieldKey)}
                                  size={buttonSize}
                                />
                              );
                            }}
                          />
                          <FormErrorMessage>
                            {errors.variants?.[index]?.sizes?.[sizeIndex]?.weight?.message}
                          </FormErrorMessage>
                        </FormControl>
                      </GridItem>
                    </Grid>

                    <Grid
                      templateColumns={{
                        base: '1fr',
                        lg: 'repeat(2, 1fr)',
                      }}
                      alignItems="center"
                      gap={spacing}
                    >
                      <GridItem>
                        <FormControl
                          isInvalid={!!errors.variants?.[index]?.sizes?.[sizeIndex]?.sku}
                        >
                          <FormLabel fontSize={labelFontSize} color="text.secondary">
                            SKU(Optional)
                          </FormLabel>
                          <Controller
                            name={`variants.${index}.sizes.${sizeIndex}.sku`}
                            control={control}
                            render={({ field }) => {
                              const fieldKey = `sku-${sizeIndex}`;
                              const displayValue = field.value ?? '';
                              return (
                                <Input
                                  placeholder="Enter SKU"
                                  value={displayValue}
                                  onChange={(e) => field.onChange(e.target.value)}
                                  onFocus={() => handleFocus(fieldKey)}
                                  onBlur={() => handleBlur(fieldKey)}
                                  size={buttonSize}
                                />
                              );
                            }}
                          />
                          <FormErrorMessage>
                            {errors.variants?.[index]?.sizes?.[sizeIndex]?.sku?.message}
                          </FormErrorMessage>
                        </FormControl>
                      </GridItem>

                      <GridItem>
                        <HStack justify="space-between" align="center">
                          {index === 0 && (
                            <FormControl>
                              <FormLabel>
                                <HStack spacing={1} align="center">
                                  <Text fontSize={labelFontSize} color="text.secondary">
                                    Primary
                                  </Text>
                                  <Tooltip
                                    label="This will be the price shown for this product"
                                    placement="auto"
                                    hasArrow
                                    openDelay={200}
                                    closeDelay={300}
                                    bg={'background.white'}
                                    textColor={'primary'}
                                    border={'1px'}
                                    borderColor={'border.default'}
                                    borderRadius={'md'}
                                    gutter={8}
                                  >
                                    <Box display="inline-block" position="relative">
                                      <Icon as={InfoCircleIcon} boxSize={4} cursor="help" />
                                    </Box>
                                  </Tooltip>
                                </HStack>
                              </FormLabel>
                              <Radio
                                isChecked={sizeIndex === primarySizeIndex}
                                onChange={() => handlePrimaryChange(sizeIndex)}
                                size={buttonSize}
                                colorScheme="brand.primary"
                              />
                            </FormControl>
                          )}
                          <HStack gap={1} align="center">
                            <Button
                              p={0}
                              m={0}
                              color="status.error"
                              onClick={addSize}
                              isDisabled={
                                sizesLoading ||
                                sizeFields.length >= sizes.length ||
                                isSingleSizeMode
                              }
                              size={buttonSize}
                            >
                              <Text color="status.error" fontSize={{ base: 24, md: 30 }} mb={1}>
                                +
                              </Text>
                            </Button>
                            <Icon
                              cursor={
                                index === 0 && sizeIndex === primarySizeIndex
                                  ? 'not-allowed'
                                  : 'pointer'
                              }
                              onClick={() => handleVariantSizeDelete(sizeIndex, sizeFields.length)}
                              color={'status.error'}
                              boxSize={6}
                              opacity={index === 0 && sizeIndex === primarySizeIndex ? 0.4 : 1}
                            >
                              <DeleteIcon />
                            </Icon>
                          </HStack>
                        </HStack>
                      </GridItem>
                    </Grid>

                    {discountPercent > 0 && (
                      <Box>
                        <Text fontSize={labelFontSize} color="text.secondary" fontWeight="medium">
                          Final Price ({discountPercent}% OFF):{' '}
                          <Text
                            as="span"
                            color="text.secondary"
                            fontWeight="medium"
                            fontSize={{ base: 'sm', md: 'md' }}
                          >
                            {currencySymbol}
                            {(() => {
                              const price =
                                Number(
                                  watch(`variants.${index}.sizes.${sizeIndex}.variantPrice`),
                                ) || 0;
                              const effectiveDiscount = Math.min(discountPercent, 100);
                              const finalPrice = price * (1 - effectiveDiscount / 100);
                              return finalPrice > 0 ? finalPrice.toFixed(2) : '0.00';
                            })()}
                          </Text>
                        </Text>
                      </Box>
                    )}
                  </VStack>
                </GridItem>
              </Grid>
            </VStack>
          </Flex>
        ))}

        <CustomConfirmationModal
          isLoading={false}
          isOpen={isOpen}
          onClose={() => {
            onClose();
            setDeleteSizeInfo(null);
          }}
          onConfirm={handleVariantSizeDeleteConfirm}
          icon={ConfirmDeleteIcon}
          title="Delete Size?"
          subtitle="Are you sure you want to delete this size?"
          description="If this is the last size, the associated variant will also be removed."
          buttonText="Confirm"
        />
        {(() => {
          const hasCustomSizeOnly = currentVariantSizes?.some((s) => {
            const sizeOption = sizes.find((opt: any) => String(opt.id) === String(s?.size));
            return sizeOption?.value === 'Custom Size Only';
          });

          return hasCustomSizeOnly && !isCustomizable ? (
            <Flex
              align="center"
              gap={2}
              bg="brand.secondary.50"
              border="1px"
              borderColor="brand.secondary.DEFAULT"
              color="orange.800"
              px={3}
              py={2}
              borderRadius="md"
              fontSize="sm"
              fontWeight="medium"
              mt={2}
              boxShadow="sm"
            >
              <Icon as={InfoCircleIcon} boxSize={4} />
              <Text>
                “Custom Size Only” selected — please enable the{' '}
                <Text as="span" fontWeight="bold" color="brand.primary.DEFAULT">
                  Customize
                </Text>{' '}
                option below to allow customers to enter their measurements.
              </Text>
            </Flex>
          ) : null;
        })()}
        <Button
          ref={addSizeLinkRef}
          border="none"
          id="addSizeLink"
          onClick={addSize}
          w="fit-content"
          color="brand.primary.DEFAULT"
          textDecor="underline"
          isDisabled={sizesLoading || sizeFields.length >= sizes.length || isSingleSizeMode}
          size={buttonSize}
        >
          {sizeFields.length === 0 ? '+ Add size' : '+ Add another size'}
        </Button>
      </Flex>
    </Box>
  );
};

export default SizeDetailsSection;
